const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_ORIGIN = new URL(BACKEND_URL).origin;
const SHARE_ASSET_PREFIX = '/api/development-report-share/';
const INTERNAL_REPORT_ASSET = /^\/api\/development-reports\/[^/]+\/asset\/[^/]+$/;
// Only these authenticated file endpoints may receive the Bearer token.
const PROTECTED_FILE_PATHS = [
  INTERNAL_REPORT_ASSET,
  /^\/api\/athletes\/[^/]+\/profile-photo\/(file|thumb)\/[^/]+$/,
  /^\/api\/attachments\/[^/]+(\/[^/]+)+$/,
  /^\/api\/wellness\/files\/[^/]+$/,
  /^\/api\/media\/photos\/file\/[^/]+\/\d{4}\/\d{1,2}\/[^/]+$/,
];

export const absoluteAssetUrl = (source) => {
  if (!source) return '';
  if (/^https?:\/\//i.test(source)) return source;
  return `${BACKEND_URL}${source.startsWith('/') ? source : `/${source}`}`;
};

const parseBackendUrl = (source) => {
  if (typeof source !== 'string' || !source || source.startsWith('//') || source.includes('\\')) return null;
  if (/(^|\/)(\.|%2e){1,2}(\/|$)/i.test(source.split(/[?#]/)[0])) return null;
  try {
    const parsed = new URL(absoluteAssetUrl(source));
    if (parsed.origin !== BACKEND_ORIGIN || parsed.username || parsed.password) return null;
    return parsed;
  }
  catch (_) { return null; }
};

export const isCapabilityAsset = (source) => {
  const parsed = parseBackendUrl(source);
  return !!parsed && parsed.pathname.startsWith(SHARE_ASSET_PREFIX);
};

export const isInternalReportAsset = (source) => {
  const parsed = parseBackendUrl(source);
  return !!parsed && INTERNAL_REPORT_ASSET.test(parsed.pathname);
};

export const isProtectedAsset = (source) => {
  const parsed = parseBackendUrl(source);
  if (!parsed || parsed.search || parsed.hash) return false;
  return PROTECTED_FILE_PATHS.some((pattern) => pattern.test(parsed.pathname));
};

const isBackendUrl = (source) => !!parseBackendUrl(source);

const responseFilename = (header, fallback) => {
  const utf = (header || '').match(/filename\*=UTF-8''([^;]+)/i);
  if (utf) { try { return decodeURIComponent(utf[1]); } catch (_) { /* noop */ } }
  const plain = (header || '').match(/filename="?([^";]+)"?/i);
  return plain?.[1] || fallback || 'evidence';
};

const STATUS_MESSAGES = {
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to open this file.',
  404: 'This file could not be found.',
};

const assetError = (status, message) => Object.assign(new Error(message || STATUS_MESSAGES[status] || 'This file is unavailable.'), { status });

const fetchProtected = async (url, fallbackName) => {
  const token = localStorage.getItem('token');
  if (!token) throw assetError(401, 'Sign in to open this file.');
  let response;
  try {
    response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      cache: 'no-store',
    });
  }
  catch (_) { throw assetError(0, 'This file could not be loaded. Check your connection and try again.'); }
  if (!response.ok) throw assetError(response.status);
  return {
    blob: await response.blob(),
    contentType: response.headers.get('content-type') || 'application/octet-stream',
    fileName: responseFilename(response.headers.get('content-disposition'), fallbackName),
  };
};

export const fetchAssetBlob = async (source, fallbackName) => {
  const url = absoluteAssetUrl(source);
  if (!url) throw new Error('Evidence URL is missing');
  if (isCapabilityAsset(source)) {
    const response = await fetch(url, { credentials: 'omit', referrerPolicy: 'no-referrer', redirect: 'error' });
    if (!response.ok) throw assetError(response.status, 'Evidence is unavailable');
    return {
      blob: await response.blob(),
      contentType: response.headers.get('content-type') || 'application/octet-stream',
      fileName: responseFilename(response.headers.get('content-disposition'), fallbackName),
    };
  }
  if (isProtectedAsset(source)) return fetchProtected(url, fallbackName);
  if (isBackendUrl(source) || source.startsWith('//')) throw assetError(400, 'Unsupported protected file address.');
  const response = await fetch(url, { credentials: 'omit', referrerPolicy: 'no-referrer' });
  if (!response.ok) throw new Error('Evidence is unavailable');
  return { blob: await response.blob(), contentType: response.headers.get('content-type') || '', fileName: fallbackName || 'evidence' };
};

export const isInlineContent = (contentType = '') => contentType.startsWith('application/pdf') || contentType.startsWith('image/');

export const downloadBlob = (blob, fileName) => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName || 'evidence';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
};

export const assetNavigationHref = (source, fileName) => {
  if (!isProtectedAsset(source)) return absoluteAssetUrl(source);
  const params = new URLSearchParams({ source: new URL(absoluteAssetUrl(source)).pathname, name: fileName || 'file' });
  const base = isInternalReportAsset(source) ? '/development-report-assets/open' : '/protected-files/open';
  return `${base}?${params.toString()}`;
};
